import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../services/supabase';
import { extractFunctionErrorMessage } from '../utils/supabaseFunctionError';
import { clearUserOfflineData } from '../services/offline/offlineDb';
import { clearUserOfflineFertilizantesData } from '../services/offline/offlineDbFertilizantes';

export const AuthContext = createContext(null);

function mapAuthError(error) {
  const msg = (error?.message || '').toLowerCase();

  if (msg.includes('invalid login credentials')) {
    return 'Correo o contraseña incorrectos.';
  }
  if (msg.includes('email not confirmed')) {
    return 'Debes confirmar tu correo electrónico antes de iniciar sesión. Revisa tu bandeja de entrada.';
  }
  if (msg.includes('already registered')) {
    return 'Ya existe una cuenta creada con este correo electrónico.';
  }
  if (msg.includes('password should be at least')) {
    return 'La contraseña debe tener al menos 8 caracteres.';
  }
  if (msg.includes('failed to fetch') || msg.includes('network')) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión a internet e intenta nuevamente.';
  }

  return 'Ocurrió un error. Intenta nuevamente.';
}

const GENERIC_PROFILE_ERROR =
  'No pudimos verificar tu acceso a la App de Campo. Intenta nuevamente o comunícate con el administrador.';

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [encargado, setEncargado] = useState(null);
  const [productor, setProductor] = useState(null);
  const [solicitud, setSolicitud] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [loading, setLoading] = useState(true);

  const lastUserIdRef = useRef(null);

  // Autorización real:
  // - ADMIN (public.usuarios_intranet) tiene prioridad total y no requiere
  //   (ni debe) existir también en encargados.
  // - Si no es admin, SOLO public.encargados decide si alguien puede entrar
  //   a App Campo y para qué productor.
  const loadProfile = useCallback(async (currentUser) => {
    setLoading(true);
    setProfileError(null);

    try {
      // PASO 1 — ¿Es administrador?
      const { data: adminRow, error: adminError } = await supabase
        .from('usuarios_intranet')
        .select('id, nombre, rol, activo')
        .eq('auth_user_id', currentUser.id)
        .eq('rol', 'ADMIN')
        .eq('activo', true)
        .maybeSingle();

      if (adminError) throw adminError;

      if (adminRow) {
        setAdmin(adminRow);
        setEncargado(null);
        setProductor(null);
        setSolicitud(null);
        return;
      }

      setAdmin(null);

      // PASO 2 — ¿Es un encargado autorizado?
      const { data: encargadoRow, error: encargadoError } = await supabase
        .from('encargados')
        .select('id, nombre, activo, idproductor')
        .eq('auth_user_id', currentUser.id)
        .maybeSingle();

      if (encargadoError) throw encargadoError;

      if (encargadoRow) {
        setSolicitud(null);

        if (!encargadoRow.activo) {
          setEncargado(encargadoRow);
          setProductor(null);
          setProfileError('Tu acceso como Encargado de Campo se encuentra deshabilitado.');
          return;
        }

        const { data: productorRow, error: productorError } = await supabase
          .from('productores')
          .select('id, nombre, activo')
          .eq('id', encargadoRow.idproductor)
          .maybeSingle();

        if (productorError) throw productorError;

        if (!productorRow || !productorRow.activo) {
          setEncargado(encargadoRow);
          setProductor(null);
          setProfileError(
            'La configuración de tu cuenta debe ser revisada por el administrador (productor no disponible).'
          );
          return;
        }

        setEncargado(encargadoRow);
        setProductor(productorRow);
        return;
      }

      // PASO 3 — Todavía no es admin ni encargado: revisar/crear solicitud.
      setEncargado(null);
      setProductor(null);

      const { data: solicitudRow, error: solicitudError } = await supabase
        .from('solicitudes_encargado')
        .select('id, nombre, email, estado, motivo_rechazo, created_at')
        .eq('auth_user_id', currentUser.id)
        .maybeSingle();

      if (solicitudError) throw solicitudError;

      if (solicitudRow) {
        setSolicitud(solicitudRow);
        return;
      }

      // No hay solicitud todavía: crearla (idempotente) usando el nombre
      // guardado como conveniencia en el registro. Esto NUNCA autoriza nada
      // por sí solo: solo deja la petición en estado PENDIENTE para que un
      // administrador la revise.
      const nombre = currentUser.user_metadata?.nombre;

      if (!nombre) {
        setSolicitud(null);
        setProfileError(GENERIC_PROFILE_ERROR);
        return;
      }

      const { data: result, error: fnError } = await supabase.functions.invoke('crear-solicitud-encargado', {
        body: { nombre },
      });

      if (fnError) {
        const message = await extractFunctionErrorMessage(fnError);
        setSolicitud(null);
        setProfileError(message || GENERIC_PROFILE_ERROR);
        return;
      }

      if (!result?.solicitud) {
        setSolicitud(null);
        setProfileError(GENERIC_PROFILE_ERROR);
        return;
      }

      setSolicitud(result.solicitud);
    } catch {
      setAdmin(null);
      setEncargado(null);
      setProductor(null);
      setSolicitud(null);
      setProfileError('No pudimos cargar tu información. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      const newUser = newSession?.user ?? null;
      setUser(newUser);

      if (!newUser) {
        lastUserIdRef.current = null;
        setAdmin(null);
        setEncargado(null);
        setProductor(null);
        setSolicitud(null);
        setProfileError(null);
        setLoading(false);
        return;
      }

      // Solo recargar el perfil cuando cambia el usuario autenticado, para
      // no repetir consultas en cada TOKEN_REFRESHED/USER_UPDATED.
      if (lastUserIdRef.current !== newUser.id) {
        lastUserIdRef.current = newUser.id;
        loadProfile(newUser);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile]);

  const signIn = useCallback(async ({ email, password }) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { ok: false, message: mapAuthError(error) };
    }
    return { ok: true };
  }, []);

  const signUp = useCallback(async ({ nombre, email, password }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // nombre viaja como conveniencia para poder crear la solicitud de
        // acceso más adelante (incluso tras confirmar el correo en otra
        // sesión). Nunca se usa para autorizar nada: la autorización real
        // siempre viene de public.encargados.
        data: { nombre },
      },
    });

    if (error) {
      return { ok: false, message: mapAuthError(error) };
    }

    return { ok: true, needsEmailConfirmation: !data.session };
  }, []);

  const signOut = useCallback(async () => {
    // Muy importante: limpiar SOLO los datos offline de este usuario antes
    // de cerrar sesión, para que si otra persona inicia sesión después en
    // el mismo dispositivo no pueda ver aplicaciones pendientes ajenas.
    const currentUserId = lastUserIdRef.current;
    if (currentUserId) {
      await clearUserOfflineData(currentUserId).catch(() => {});
      await clearUserOfflineFertilizantesData(currentUserId).catch(() => {});
    }
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(() => {
    if (user) {
      return loadProfile(user);
    }
    return Promise.resolve();
  }, [user, loadProfile]);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      admin,
      isAdmin: Boolean(admin),
      encargado,
      productor,
      solicitud,
      profileError,
      signIn,
      signUp,
      signOut,
      refreshProfile,
    }),
    [
      user,
      session,
      loading,
      admin,
      encargado,
      productor,
      solicitud,
      profileError,
      signIn,
      signUp,
      signOut,
      refreshProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
